import { buildR2AssetsImageUrl } from "@saas/shared/lib/image-url-builder";
import type {
	I2VAspectRatio,
	I2VMode,
	I2VModelId,
	I2VResolution,
} from "../types";

/**
 * 示例区的素材，全部由我们自己的模型跑出来，存在 R2 的
 * imagesart-assets/templates/image-to-video/samples/ 下。
 *
 * 这份数组由 packages/scripts/i2v-samples 的 `--stage emit-config` 生成后粘贴进来。
 * 不要手改字段值——改了会和 R2 上的实际产物对不上。要改就改那边的 manifest.ts 重跑。
 */
const SAMPLES_BASE = "templates/image-to-video/samples";

/**
 * `v` 是产物内容的短哈希。R2 的 key 固定，换了内容 URL 不变，Cloudflare 边缘
 * （max-age=14400）和浏览器都会继续吐旧文件——2026-09-12 重跑完七条，页面上
 * 三条视频和全部七张封面还是上一版，就是这么来的。带上哈希后内容一变 URL 就变。
 * 这些值由 `--stage emit-config` 从本地产物算出来，不要手填。
 */
const inputUrl = (slot: string, v: string) =>
	`${buildR2AssetsImageUrl(`${SAMPLES_BASE}/inputs/${slot}.webp`)}?v=${v}`;
const clipUrl = (id: string, v: string) =>
	`${buildR2AssetsImageUrl(`${SAMPLES_BASE}/clips/${id}.mp4`)}?v=${v}`;
const posterUrl = (id: string, v: string) =>
	`${buildR2AssetsImageUrl(`${SAMPLES_BASE}/clips/posters/${id}.jpg`)}?v=${v}`;

export interface I2VSampleExample {
	id: string;
	title: string;
	/** 点 Create 时把表单切到这个输入范式 */
	mode: I2VMode;
	/** 跑这条素材用的模型，一并回填，保证用户点 Create 后能复现同样的效果 */
	modelId: I2VModelId;
	/** 已在跑批时校验过 ≤ I2V_PROMPT_MAX_LENGTH，否则回填后用户提交不了 */
	prompt: string;
	/**
	 * 按槽位顺序排列：between 是 [首帧, 尾帧]，reference 是整组参考图。
	 * 必须是公网绝对地址——它会原样进 generate 请求，服务端用 z.string().url() 卡相对路径。
	 */
	inputImages: string[];
	poster: string;
	videoUrl: string;
	resolution: I2VResolution;
	/** 只有 reference 模式有 */
	aspectRatio?: I2VAspectRatio;
	duration: number;
	/** 参考视频（/genjutsu-ai 的样片），地址要在 NEXT_PUBLIC_R2_PUBLIC_URL 下 */
	referenceMedia?: {
		kind: "video" | "audio";
		url: string;
		durationSec: number;
	}[];
}

export const i2vSampleExamples: I2VSampleExample[] = [
	{
		id: "sample-01",
		title: "Underwater Portrait",
		mode: "start",
		modelId: "master-3.0",
		prompt: "He keeps his eyes open, looking straight into the lens, with just one quick natural blink. The camera slowly pushes in until his face nearly fills the frame. A goldfish glides right past his nose and another drifts behind his shoulder, his hair sways gently with the water, a few small bubbles rise from his nostrils. Sunlight caustics ripple across his skin, calm and dreamy, photorealistic underwater.",
		inputImages: [inputUrl("sample-01-1", "554029c4")],
		poster: posterUrl("sample-01", "4e4010f2"),
		videoUrl: clipUrl("sample-01", "836fc61d"),
		resolution: "720p",
		duration: 5,
	},
	{
		id: "sample-02",
		title: "Kittens in the Sink",
		mode: "start",
		modelId: "master-3.0",
		prompt: "The camera slowly zooms in to frame the two kittens. They stand up in the pool and throw themselves into an impassioned slow-groove dance with popping moves, each move different from the last: alternating paw raises, shoulder rolls, hip sways, little bounces. Their mouths open and close naturally in an adorable, flirty way; their movements are charming and anthropomorphic, playful rather than aggressive, while the water keeps flowing.",
		inputImages: [inputUrl("sample-02-1", "54dafac2")],
		poster: posterUrl("sample-02", "6d2c3e6d"),
		videoUrl: clipUrl("sample-02", "2f6858b2"),
		resolution: "720p",
		duration: 10,
	},
	{
		id: "sample-03",
		title: "Summer Drink Commercial",
		mode: "reference",
		modelId: "master-3.0",
		prompt: "The woman in @Image 1 is the lead character. Generate an ads video for the canned drink BLUE TIDE based on the storyboard in @Image 2.",
		inputImages: [
			inputUrl("sample-03-1", "c3456dca"),
			inputUrl("sample-03-2", "8ce36cf7"),
		],
		poster: posterUrl("sample-03", "19a64fcb"),
		videoUrl: clipUrl("sample-03", "cbcd219a"),
		resolution: "720p",
		aspectRatio: "16:9",
		duration: 15,
	},
	{
		id: "sample-04",
		title: "Red Carpet Walk",
		mode: "start",
		modelId: "master-3.0",
		prompt: "A woman walks the red carpet as reporters snap photos enthusiastically and camera flashes keep going off around her. She keeps moving forward with her dog while the camera circles them, and only at the very end do they stop and strike a pose together. She is the star and the visual focal point of the entire scene, captured in the cinematic style of a high-fashion editorial.",
		inputImages: [inputUrl("sample-04-1", "ee946d90")],
		poster: posterUrl("sample-04", "67342783"),
		videoUrl: clipUrl("sample-04", "4ff82688"),
		resolution: "720p",
		duration: 8,
	},
	{
		id: "sample-05",
		title: "Snow Chase",
		mode: "start",
		modelId: "master-3.0",
		prompt: "She speeds across the snow on the snowmobile while the yeti gives chase, knocking down trees along the path. Amid the swirling snow and wind the camera stays fixed on her; her expression is natural and realistic, a mix of terror and focus as she rides forward and keeps glancing back over her shoulder at her pursuer. Tense cinematic action.",
		inputImages: [inputUrl("sample-05-1", "cb82bd80")],
		poster: posterUrl("sample-05", "b4091745"),
		videoUrl: clipUrl("sample-05", "39947d27"),
		resolution: "720p",
		duration: 10,
	},
	{
		id: "sample-06",
		title: "Breaking News",
		mode: "between",
		modelId: "master-3.0",
		prompt: "The burning airliner plummets trailing black smoke. The camera pulls back fast and the falling plane turns out to be the front-page photo of a newspaper held by a man sitting alone in a dry field. The masthead reads DAILY GLOBAL NEWS and the headline reads BREAKING NEWS PASSENGER PLANE CRASHES in clean sharp print. The page catches fire right where the plane photo is. He senses something is wrong, flings the paper aside and jumps to his feet, knocking the white chair over behind him, then pats his jacket down to check he is not burned while the paper smolders on the ground. The video does not need to strictly match the start and end frames; keep the visual flow smooth and natural.",
		inputImages: [
			inputUrl("sample-06-1", "f245365c"),
			inputUrl("sample-06-2", "70bb1bcd"),
		],
		poster: posterUrl("sample-06", "9972c721"),
		videoUrl: clipUrl("sample-06", "4f51eb5d"),
		resolution: "720p",
		duration: 8,
	},
	{
		id: "sample-07",
		title: "Neon City Chase",
		mode: "start",
		modelId: "master-3.0",
		prompt: "0-3s: he leaps off the rooftop edge and free-falls fast, arms flailing, wind whipping his hoodie, neon towers streaking upward past him. 3-5s: a glowing hoverboard assembles under his feet mid-fall from locking mechanical parts, thrusters ignite in blue flame and catch him; he wobbles and spreads his arms for balance. 5-10s: he rides the board weaving between the towers at high speed, neon billboards and holograms streaking past in motion blur, banking hard through a narrow gap between two buildings. Third-person chase cam close behind, shaky and inertial. Cinematic AAA game CG, night cyberpunk megacity, tense and fast.",
		inputImages: [inputUrl("sample-07-1", "b77518d3")],
		poster: posterUrl("sample-07", "42d23cf0"),
		videoUrl: clipUrl("sample-07", "59cfb442"),
		resolution: "720p",
		duration: 10,
	},
];

// ---------------------------------------------------------------------------
// 下面这些不是 `--stage emit-config` 生成的，手改安全。
//
// 素材在 R2 上是 1280×720 的原图，页面从来没按原尺寸显示过，所以一律走
// `buildOptimizedImageUrl` 要缩放 + WebP 的版本（2026-09-15 起）。宽度写在这里
// 而不是各组件里，是因为首屏那张 poster 被两处引用：page.tsx 的 <link preload>
// 和 I2VSampleShowcase 的 <video poster>。**两边必须是同一个 URL**，差一个字符就
// 会下两份图，preload 反而变成纯浪费。
//
// 取值必须落在 Next 默认的 deviceSizes/imageSizes 上，否则优化器回 400，判据见
// `buildOptimizedImageUrl` 的注释。
// ---------------------------------------------------------------------------

/** 首屏大预览：容器最宽 max-w-3xl(768px)，828 是刚好盖住它的那档 deviceSize */
export const I2V_POSTER_WIDTH = 828;

/** 底部缩略图：容器 w-32 / sm:w-36（128–144px），384 够 2x 屏 */
export const I2V_THUMB_WIDTH = 384;

/** 入场覆盖层里的参考图：h-14 w-14 / sm:h-20 w-20（56–80px），256 够 2x 屏 */
export const I2V_INTRO_IMAGE_WIDTH = 256;

/** 模式区块（折叠下方）的三条对照视频：容器 md:w-1/2，640 够用 */
export const I2V_MODES_POSTER_WIDTH = 640;
